import { getClassLevel } from '../../classIdentity.mjs';

// 2024 Life Domain (SRD 5.2.1). Domain spells are always prepared through feature grants (no free casts). Disciple of
// Life, Blessed Healer and Supreme Healing are read by the healing spell service; Preserve Life spends a Channel Divinity use.
export const LIFE_SUBCLASS_ID = 'life';
export const DISCIPLE_OF_LIFE_FEATURE_ID = 'cleric:life:disciple-of-life';
export const PRESERVE_LIFE_FEATURE_ID = 'cleric:life:preserve-life';
export const BLESSED_HEALER_FEATURE_ID = 'cleric:life:blessed-healer';
export const SUPREME_HEALING_FEATURE_ID = 'cleric:life:supreme-healing';
export const DOMAIN_SPELL_GRANT_PREFIX = 'cleric-life-';
export const PRESERVE_LIFE_RANGE_FEET = 30;
/** Life Domain spells by Cleric level. */
export const LIFE_DOMAIN_SPELLS = Object.freeze({
  3: Object.freeze(['aid', 'bless', 'cure-wounds', 'lesser-restoration']),
  5: Object.freeze(['mass-healing-word', 'revivify']),
  7: Object.freeze(['aura-of-life', 'death-ward']),
  9: Object.freeze(['greater-restoration', 'mass-cure-wounds']),
});

const isLife = character => {
  const state = character?.classState?.cleric;
  const raw = state?.subclassId ?? state?.subclass?.id ?? (typeof state?.subclass === 'string' ? state.subclass : null);
  return String(raw || '').trim().toLowerCase() === LIFE_SUBCLASS_ID;
};

/** Domain spells this Cleric has prepared through the Life Domain, lowest level first. */
export const getLifeDomainSpells = character => {
  if (!isLife(character)) return [];
  const level = getClassLevel(character, 'cleric');
  return Object.entries(LIFE_DOMAIN_SPELLS).filter(([gained]) => level >= Number(gained)).flatMap(([, spells]) => spells);
};
export const domainSpellGrantId = spellId => `${DOMAIN_SPELL_GRANT_PREFIX}${spellId}`;
export const domainSpellForGrant = grantId => (String(grantId || '').startsWith(DOMAIN_SPELL_GRANT_PREFIX) ? String(grantId).slice(DOMAIN_SPELL_GRANT_PREFIX.length) : null);

/** Disciple of Life and Blessed Healer both restore 2 plus the slot's level. */
export const slotHealingBonus = slotLevel => 2 + Number(slotLevel);
/** Preserve Life's pool: five times the Cleric's level. */
export const preserveLifePool = level => 5 * level;
export const isBloodied = (currentHp, maximumHp) => maximumHp > 0 && currentHp * 2 <= maximumHp;
/** Hit Points Preserve Life can restore to a creature: it stops at half the creature's maximum. */
export const preserveLifeRoom = (currentHp, maximumHp) => Math.max(0, Math.floor(maximumHp / 2) - currentHp);

const life = Object.freeze({
  id: LIFE_SUBCLASS_ID, definitionId: 'cleric:life', classId: 'cleric',
  name: 'Life Domain', selectionLevel: 3,
  description: 'Soothe the hurts of the world.',
  features: Object.freeze([
    {
      id: 'cleric:life:domain-spells', name: 'Life Domain Spells', level: 3,
      description: 'Your connection to this divine domain ensures you always have certain spells ready. When you reach a Cleric level specified in the Life Domain Spells table, you thereafter always have the listed spells prepared: Aid, Bless, Cure Wounds and Lesser Restoration (3), Mass Healing Word and Revivify (5), Aura of Life and Death Ward (7), Greater Restoration and Mass Cure Wounds (9).',
      presentation: { kind: 'passive' }, contracts: [],
    },
    {
      id: DISCIPLE_OF_LIFE_FEATURE_ID, name: 'Disciple of Life', level: 3,
      description: 'When a spell you cast with a spell slot restores Hit Points to a creature, that creature regains additional Hit Points on the turn you cast the spell. The additional Hit Points equal 2 plus the spell slot’s level.',
      presentation: { kind: 'passive' }, contracts: [],
    },
    {
      id: PRESERVE_LIFE_FEATURE_ID, name: 'Preserve Life', level: 3,
      description: 'As a Magic action, you present your Holy Symbol and expend a use of your Channel Divinity to evoke healing energy. Choose Bloodied creatures within 30 feet of yourself (which can include you), and divide a pool of Hit Points among them. The pool equals five times your Cleric level. This healing can’t restore a creature to more than half its Hit Point maximum.',
      presentation: { kind: 'action' }, contracts: [],
    },
    {
      id: BLESSED_HEALER_FEATURE_ID, name: 'Blessed Healer', level: 6,
      description: 'The healing spells you cast on others heal you as well. Immediately after you cast a spell with a spell slot that restores Hit Points to one creature other than you, you regain Hit Points equal to 2 plus the spell slot’s level.',
      presentation: { kind: 'passive' }, contracts: [],
    },
    {
      id: SUPREME_HEALING_FEATURE_ID, name: 'Supreme Healing', level: 17,
      description: 'When you would normally roll one or more dice to restore Hit Points to a creature with a spell or Channel Divinity, don’t roll those dice for the healing; instead use the highest number possible for each die. For example, instead of restoring 2d6 Hit Points to a creature, you restore 12.',
      presentation: { kind: 'passive' }, contracts: [],
    },
  ]),
});

export default life;
